/**
 * ParticleData.ts
 *
 * Particle system configuration extracted from Construct 3.
 * Provides types and helper functions for using particle effects in Phaser.
 *
 * Data Source:
 * - assets/data/particles-c3data.json (auto-generated by `npx tsx scripts/c3-extract.ts particles`)
 *
 * Usage:
 *   const def = ParticleData.get(scene, 'Particles5');
 *   const emitter = ParticleData.createEmitter(scene, 'Particles5', x, y);
 */

import Phaser from 'phaser';
import { LayerDepths } from '../constants/enums';

// ============================================================================
// Types
// ============================================================================

export interface ParticleDefinition {
  /** Particle system name */
  name: string;
  /** Image file name (e.g., "particles5.png") */
  image: string;
  /** Image dimensions */
  imageSize: { width: number; height: number };
  /** Emission type: one-shot (burst) or continuous */
  emissionType: 'one-shot' | 'continuous';
  /** Particles per emission/second */
  rate: number;
  /** Spray cone angle in degrees */
  sprayCone: number;
  /** Initial speed */
  speed: number;
  /** Speed randomizer (0-100) */
  speedRandom: number;
  /** Initial size (percentage of original image) */
  size: number;
  /** Size randomizer */
  sizeRandom: number;
  /** Growth rate per second */
  growRate: number;
  /** Growth rate randomizer */
  growRateRandom: number;
  /** Initial opacity (0-100) */
  opacity: number;
  /** Opacity randomizer */
  opacityRandom: number;
  /** Acceleration (negative = decelerate) */
  acceleration: number;
  /** Gravity (0 = float, positive = fall) */
  gravity: number;
  /** Angle randomizer in degrees */
  angleRandom: number;
  /** X position randomizer */
  xRandom: number;
  /** Y position randomizer */
  yRandom: number;
  /** Lifespan in seconds */
  lifespan: number;
  /** Life speed randomizer */
  lifespanRandom: number;
  /** Destroy mode */
  destroyMode: 'fade' | 'destroy';
  /** Effects applied (e.g., HSL adjust) */
  effects: string[];
  /** Behaviors (e.g., Pin) */
  behaviors: string[];
}

interface ParticlesDataFile {
  _meta: {
    extractedAt: string;
    source: string;
    warning: string;
  };
  particles: Record<string, ParticleDefinition>;
}

// ============================================================================
// Particle Data Manager
// ============================================================================

/**
 * Manages particle system definitions loaded from particles-c3data.json
 */
export class ParticleData {
  private static definitions: Map<string, ParticleDefinition> | null = null;

  /**
   * Load particle definitions from JSON cache (with overrides)
   */
  static load(scene: Phaser.Scene): void {
    if (this.definitions) return;

    const baseData = scene.cache.json.get('particles-c3data') as ParticlesDataFile | undefined;
    const overrides = scene.cache.json.get('particles-overrides') as ParticlesDataFile | undefined;

    if (!baseData) {
      console.warn('[ParticleData] particles-c3data.json not loaded');
      this.definitions = new Map();
      return;
    }

    // Start with base particles
    const merged: Record<string, ParticleDefinition> = { ...baseData.particles };

    // Merge overrides (overwrite or add new particles)
    if (overrides?.particles) {
      for (const [key, value] of Object.entries(overrides.particles)) {
        merged[key] = { ...merged[key], ...value };
      }
    }

    this.definitions = new Map(Object.entries(merged));
  }

  /**
   * Get a particle definition by name
   */
  static get(name: string): ParticleDefinition | undefined {
    return this.definitions?.get(name);
  }

  /**
   * Get all particle definition names
   */
  static getAll(): string[] {
    return this.definitions ? Array.from(this.definitions.keys()) : [];
  }

  /**
   * Check if a particle definition exists
   */
  static has(name: string): boolean {
    return this.definitions?.has(name) ?? false;
  }

  /**
   * Get the texture key for a particle (without .png extension)
   */
  static getTextureKey(name: string): string {
    const def = this.get(name);
    if (!def) return name.toLowerCase();
    return def.image.replace('.png', '');
  }

  /**
   * Create a Phaser particle emitter configured from the definition
   *
   * @param scene - Phaser scene
   * @param name - Particle definition name (e.g., "Particles5")
   * @param x - Initial X position
   * @param y - Initial Y position
   * @param angle - Emission angle in degrees (default: 270 = upward)
   * @returns Configured particle emitter, or null if definition not found
   */
  static createEmitter(
    scene: Phaser.Scene,
    name: string,
    x: number,
    y: number,
    angle: number = 270
  ): Phaser.GameObjects.Particles.ParticleEmitter | null {
    const def = this.get(name);
    if (!def) {
      console.warn(`[ParticleData] Unknown particle: ${name}`);
      return null;
    }

    const textureKey = this.getTextureKey(name);

    // Check if texture exists
    if (!scene.textures.exists(textureKey)) {
      console.warn(`[ParticleData] Texture not loaded: ${textureKey}`);
      return null;
    }

    // Convert C3 values to Phaser config
    const halfCone = def.sprayCone / 2;
    const lifeMs = def.lifespan * 1000;
    const scale = def.size / 100;
    const growScale = 1 + (def.growRate / 100); // C3 growRate is percentage per second

    // Create emitter at (0, 0) - emitParticleAt() will handle positioning
    // (Same pattern as AbductionEffects which works correctly)
    const emitter = scene.add.particles(0, 0, textureKey, {
      // Speed with randomization
      speed: {
        min: Math.max(0, def.speed - def.speedRandom),
        max: def.speed + def.speedRandom,
      },

      // Angle: base angle ± spray cone
      angle: {
        min: angle - halfCone,
        max: angle + halfCone,
      },

      // Scale (start -> end based on grow rate)
      scale: {
        start: scale,
        end: scale * growScale,
      },

      // Alpha (fade out for 'fade' mode)
      alpha: def.destroyMode === 'fade'
        ? { start: def.opacity / 100, end: 0 }
        : def.opacity / 100,

      // Lifespan
      lifespan: lifeMs,

      // Gravity
      gravityY: def.gravity,

      // Acceleration along movement direction
      accelerationX: def.emissionType === 'continuous' ? def.acceleration : 0,

      // Quantity and frequency
      quantity: def.emissionType === 'one-shot' ? def.rate : 1,
      frequency: def.emissionType === 'continuous' ? 1000 / def.rate : -1,

      // Start paused for one-shot, emit for continuous
      emitting: def.emissionType === 'continuous',
    });

    emitter.setDepth(LayerDepths.Effects);

    return emitter;
  }

  /**
   * Create and emit a one-shot particle burst
   *
   * @param scene - Phaser scene
   * @param name - Particle definition name
   * @param x - Emission X position
   * @param y - Emission Y position
   * @param angle - Emission angle in degrees (default: 270 = upward)
   * @param count - Number of particles (default: use definition rate)
   * @returns The emitter, or null if creation failed
   */
  static burst(
    scene: Phaser.Scene,
    name: string,
    x: number,
    y: number,
    angle: number = 270,
    count?: number
  ): Phaser.GameObjects.Particles.ParticleEmitter | null {
    const def = this.get(name);
    if (!def) {
      console.warn(`[ParticleData] Unknown particle for burst: ${name}`);
      return null;
    }

    const emitter = this.createEmitter(scene, name, x, y, angle);
    if (!emitter) {
      return null;
    }

    // Emit burst at specified position
    const particleCount = count ?? def.rate;
    emitter.emitParticleAt(x, y, particleCount);

    // Auto-destroy after lifespan (plus buffer)
    const destroyDelay = def.lifespan * 1000 + 500;
    scene.time.delayedCall(destroyDelay, () => {
      emitter.destroy();
    });

    return emitter;
  }
}
